import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Compass } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Analyses() {
    usePageMeta(
        { fr: "Analyses & ressources export — WEYB", en: "Export insights & resources — WEYB" },
        { fr: "Études de marché, retours de missions et décryptages export, publiés par Saber Bellaaj.", en: "Market studies, mission debriefs and export insights, published by Saber Bellaaj." }
    );
    const [articles, setArticles] = useState([]);
    const { t, prefix } = useLang();

    useEffect(() => {
        axios.get(`${API}/articles`).then((res) => setArticles(res.data)).catch(() => setArticles([]));
    }, []);

    return (
        <section data-testid="analyses-page" className="bg-white pt-32 md:pt-40 pb-24 md:pb-32 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("analysesPage.eyebrowIndex")}>{t("analysesPage.eyebrow")}</Eyebrow>
                <Reveal>
                    <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight text-weyb-ink leading-tight max-w-3xl">
                        {t("analysesPage.title")}
                    </h1>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl leading-relaxed">
                        {t("analysesPage.intro")}
                    </p>
                </Reveal>

                {articles.length === 0 ? (
                    <Reveal delay={0.15}>
                        <div data-testid="analyses-page-empty" className="mt-16 border border-dashed border-gray-300 rounded-sm p-16 text-center bg-[#F9FAFB]">
                            <Compass size={26} strokeWidth={1.5} className="mx-auto text-weyb-yellow" aria-hidden="true" />
                            <p className="mt-5 font-display text-2xl font-medium text-weyb-ink">
                                {t("analysesPage.emptyTitle")}
                            </p>
                            <p className="mt-3 text-sm text-gray-500 max-w-md mx-auto">
                                {t("analysesPage.emptyText")}
                            </p>
                        </div>
                    </Reveal>
                ) : (
                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        {articles.map((a, i) => (
                            <Reveal key={a.id} delay={(i % 3) * 0.08}>
                                <Link to={`${prefix}/analyses/${a.slug}`} data-testid={`analyses-card-${i}`} className="block group bg-white border border-gray-200 rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-[0_24px_48px_rgba(10,15,22,0.08)] transition-all duration-300">
                                    {a.image && (
                                        <div className="aspect-[16/9] overflow-hidden">
                                            <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        </div>
                                    )}
                                    <div className="p-7">
                                        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-weyb-yellow-dark">{a.category}</span>
                                        <h2 className="mt-3 font-display text-lg font-medium text-weyb-ink leading-snug">{a.title}</h2>
                                        <p className="mt-3 text-sm text-gray-600 leading-relaxed">{a.excerpt}</p>
                                        <p className="mt-4 text-xs text-gray-400">{a.author} — {new Date(a.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
